/**
 * PR-G — Draft preview mesh.
 *
 * Ephemeral Three.js group parented to the scene that paints the
 * "rubber-band" preview during draft commands (LINE second-point pick,
 * CIRCLE radius drag, MOVE base→target preview, grip-edit drag, etc.).
 *
 * Why a separate group instead of the BatchedMesh:
 *   - BatchedMesh holds tens of thousands of segments; updating one
 *     segment per cursor move would cost a full GPU buffer rebuild.
 *   - The draft preview is at most one segment / circle / curve at a
 *     time, so a tiny dedicated mesh is essentially free.
 *   - Selection-state texture (Q18.B) and BatchedMesh both share
 *     ownership invariants we don't want to break by hot-swapping
 *     entity slots while a draft is active.
 *
 * Lifecycle:
 *   - `createDraftPreviewMesh(scene)` — attach an empty THREE.Group
 *     to the scene; returns a handle with `update*` and `clear`.
 *   - `update*` mutate the geometry in-place; the handle keeps one
 *     mesh per primitive kind (line / circle / arc) so successive
 *     updates only touch buffer data, not allocate new objects.
 *   - `clear()` empties the group (no GPU disposal — the mesh objects
 *     are reused on the next update).
 *   - `dispose()` removes the group from the scene and disposes all
 *     geometries / materials. Called on unmount.
 *
 * The handle is the only public surface — Scene3DStage wires it into
 * the existing `onCanvasHover` / `onGripDrag` paths and clears it on
 * draft commit / cancel.
 */

import * as THREE from "three";

/** A tiny line segment preview (e.g. LINE second-point ghost). */
export interface DraftLineUpdate {
  readonly kind: "line";
  readonly a: { x: number; y: number };
  readonly b: { x: number; y: number };
  readonly colorHex?: string;
}

/** A circle preview (CIRCLE radius drag). */
export interface DraftCircleUpdate {
  readonly kind: "circle";
  readonly center: { x: number; y: number };
  readonly radius: number;
  readonly colorHex?: string;
}

/** Polyline preview (segmented preview during PLINE / RECTANG draft). */
export interface DraftPolylineUpdate {
  readonly kind: "polyline";
  readonly points: ReadonlyArray<{ x: number; y: number }>;
  readonly closed?: boolean;
  readonly colorHex?: string;
}

/**
 * Disjoint line-segment list preview — used for DIMLINEAR rubber-band
 * where multiple non-connected primitives (extension lines, dim line,
 * leader) need painting together. `LineSegments` topology (gl.LINES)
 * consumes vertex pairs so disconnected segments are first-class — no
 * fake "moveTo" continuation vertices needed as a polyline would
 * require.
 */
export interface DraftSegmentsUpdate {
  readonly kind: "segments";
  readonly segments: ReadonlyArray<{
    a: { x: number; y: number };
    b: { x: number; y: number };
  }>;
  readonly colorHex?: string;
}

export type DraftPreviewUpdate =
  | DraftLineUpdate
  | DraftCircleUpdate
  | DraftPolylineUpdate
  | DraftSegmentsUpdate;

/**
 * MC4 clip slice — axis-aligned world-space clip rect (the active VP's paper
 * rect, the SAME bounds `pointInViewport` uses). Plain numbers — the caller
 * (DraftPreviewLayer) never constructs THREE.Plane objects; the handle OWNS
 * its 4 Plane instances and mutates them in place when the rect changes
 * (#234 @codex P2 — no per-pointer-move Plane allocation / material recompile).
 */
export interface DraftClipRect {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

/**
 * Public surface of the draft preview mesh — what Scene3DStage holds
 * and what the test bridge can poke for assertions.
 */
export interface DraftPreviewMeshHandle {
  /** The THREE.Group parented to the scene. */
  readonly group: THREE.Group;
  /** Apply a new preview update (replaces the current preview). */
  update(input: DraftPreviewUpdate): void;
  /**
   * MC4 clip slice — set (or clear) the world-space clip rect applied to EVERY
   * draft-preview material. `null` removes clipping (the default).
   *
   * Used to clip an in-VP rubber band to the active viewport's paper rect:
   * the caller passes the rect's plain bounds; the handle derives 4
   * axis-aligned `THREE.Plane`s it OWNS (created once, mutated in place — see
   * `setClipRect` impl for the three.js uniform-vs-define rationale). The
   * planes apply to all already-created materials AND to any material created
   * later (so a mesh first instantiated AFTER this call still gets clipped).
   * `clipIntersection = false` ⇒ a fragment must be inside ALL planes
   * (intersection) ⇒ inside the rect. Requires `localClippingEnabled = true`
   * on the WebGLRenderer for the planes to take effect.
   */
  setClipRect(rect: DraftClipRect | null): void;
  /** Clear without disposing — reuses the meshes on next update. */
  clear(): void;
  /** Remove from scene and dispose all GPU resources. */
  dispose(): void;
}

/** Default preview color matches the existing rubber-band SVG strokes. */
const DEFAULT_PREVIEW_COLOR = 0xffd98a;

/**
 * Create a draft preview mesh attached to `scene`. The group is empty
 * until the first `update*` call.
 *
 * The returned handle is safe to call from cursor-move event handlers —
 * each `update*` mutates an existing mesh (no per-frame allocation).
 */
export function createDraftPreviewMesh(
  scene: THREE.Scene,
): DraftPreviewMeshHandle {
  const group = new THREE.Group();
  group.name = "scene3d-draft-preview";
  group.userData = { kind: "draft-preview" };
  scene.add(group);

  // MC4 clip slice — the active world-space clip rect, `null` ⇒ unclipped (the
  // default; restores pre-clip behaviour exactly). Stored BY VALUE (4 plain
  // numbers, copied on set) so `setClipRect` can no-op on an identical rect
  // without trusting the caller's object identity.
  let currentClipRect: DraftClipRect | null = null;

  // The 4 axis-aligned clip Plane instances the handle OWNS (#234 @codex P2).
  // Allocated ONCE on the first non-null rect and mutated in place afterwards;
  // the SAME array object is assigned to every material's `clippingPlanes`.
  //
  // Plane convention: `THREE.Plane(normal, constant)` keeps the half-space
  // where `normal · point + constant ≥ 0`. With `clipIntersection = false` a
  // fragment survives only when inside ALL planes — i.e. inside the rect:
  //   - [0] left   keep x ≥ minX → normal (1,0,0),  constant −minX
  //   - [1] right  keep x ≤ maxX → normal (−1,0,0), constant  maxX
  //   - [2] bottom keep y ≥ minY → normal (0,1,0),  constant −minY
  //   - [3] top    keep y ≤ maxY → normal (0,−1,0), constant  maxY
  // The normals are fixed (axis-aligned rect) — only the constants change.
  let ownedClipPlanes: THREE.Plane[] | null = null;

  function ensureOwnedClipPlanes(): THREE.Plane[] {
    if (!ownedClipPlanes) {
      ownedClipPlanes = [
        new THREE.Plane(new THREE.Vector3(1, 0, 0), 0),
        new THREE.Plane(new THREE.Vector3(-1, 0, 0), 0),
        new THREE.Plane(new THREE.Vector3(0, 1, 0), 0),
        new THREE.Plane(new THREE.Vector3(0, -1, 0), 0),
      ];
    }
    return ownedClipPlanes;
  }

  /** Write `rect` into the owned planes' constants (in-place — see above). */
  function writeRectToPlanes(planes: THREE.Plane[], rect: DraftClipRect): void {
    planes[0]!.constant = -rect.minX;
    planes[1]!.constant = rect.maxX;
    planes[2]!.constant = -rect.minY;
    planes[3]!.constant = rect.maxY;
  }

  // Apply the current clip state to a freshly-created material (the lazy
  // `ensure*` path) or on a clip TOGGLE (null↔rect in `setClipRect`).
  // Centralises the three-field write (planes + intersection mode +
  // needsUpdate) so every `ensure*` stays consistent with `setClipRect`.
  // `needsUpdate = true` is correct on BOTH paths it serves: a material
  // created lazily compiles its first program anyway, and a toggle changes
  // the plane COUNT (0↔4), i.e. the shader's NUM_CLIPPING_PLANES define —
  // a program recompile is legitimately needed (see `setClipRect`).
  function applyClipToMaterial(mat: THREE.LineBasicMaterial): void {
    mat.clippingPlanes =
      currentClipRect && ownedClipPlanes ? ownedClipPlanes : null;
    mat.clipIntersection = false;
    mat.needsUpdate = true;
  }

  // Reusable meshes — we instantiate lazily on first matching update.
  let lineMesh: THREE.Line | null = null;
  let lineGeometry: THREE.BufferGeometry | null = null;
  let lineMaterial: THREE.LineBasicMaterial | null = null;

  let circleMesh: THREE.Line | null = null;
  let circleGeometry: THREE.BufferGeometry | null = null;
  let circleMaterial: THREE.LineBasicMaterial | null = null;

  let polylineMesh: THREE.Line | null = null;
  let polylineGeometry: THREE.BufferGeometry | null = null;
  let polylineMaterial: THREE.LineBasicMaterial | null = null;

  let segmentsMesh: THREE.LineSegments | null = null;
  let segmentsGeometry: THREE.BufferGeometry | null = null;
  let segmentsMaterial: THREE.LineBasicMaterial | null = null;

  function ensureLine(): {
    mesh: THREE.Line;
    geom: THREE.BufferGeometry;
    mat: THREE.LineBasicMaterial;
  } {
    if (!lineMesh || !lineGeometry || !lineMaterial) {
      lineGeometry = new THREE.BufferGeometry();
      lineGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute([0, 0, 0, 0, 0, 0], 3),
      );
      lineMaterial = new THREE.LineBasicMaterial({
        color: DEFAULT_PREVIEW_COLOR,
        transparent: true,
        opacity: 0.8,
        depthTest: false,
        depthWrite: false,
      });
      applyClipToMaterial(lineMaterial);
      lineMesh = new THREE.Line(lineGeometry, lineMaterial);
      lineMesh.userData = { kind: "draft-line" };
      lineMesh.renderOrder = 999;
    }
    return { mesh: lineMesh, geom: lineGeometry, mat: lineMaterial };
  }

  function ensureCircle(): {
    mesh: THREE.Line;
    geom: THREE.BufferGeometry;
    mat: THREE.LineBasicMaterial;
  } {
    if (!circleMesh || !circleGeometry || !circleMaterial) {
      circleGeometry = new THREE.BufferGeometry();
      // Pre-allocate 64 segments + 1 closing vertex.
      const segments = 64;
      const positions = new Float32Array((segments + 1) * 3);
      circleGeometry.setAttribute(
        "position",
        new THREE.BufferAttribute(positions, 3),
      );
      circleMaterial = new THREE.LineBasicMaterial({
        color: DEFAULT_PREVIEW_COLOR,
        transparent: true,
        opacity: 0.8,
        depthTest: false,
        depthWrite: false,
      });
      applyClipToMaterial(circleMaterial);
      circleMesh = new THREE.Line(circleGeometry, circleMaterial);
      circleMesh.userData = { kind: "draft-circle" };
      circleMesh.renderOrder = 999;
    }
    return { mesh: circleMesh, geom: circleGeometry, mat: circleMaterial };
  }

  function ensurePolyline(): {
    mesh: THREE.Line;
    geom: THREE.BufferGeometry;
    mat: THREE.LineBasicMaterial;
  } {
    if (!polylineMesh || !polylineGeometry || !polylineMaterial) {
      polylineGeometry = new THREE.BufferGeometry();
      polylineMaterial = new THREE.LineBasicMaterial({
        color: DEFAULT_PREVIEW_COLOR,
        transparent: true,
        opacity: 0.8,
        depthTest: false,
        depthWrite: false,
      });
      applyClipToMaterial(polylineMaterial);
      // LineSegments draws gl.LINES (vertex pairs), each segment is an
      // independent pair. Avoids THREE.Line / LINE_STRIP last-segment
      // dropping observed during rectangle preview repro. Pair expansion
      // happens in the polyline update branch below.
      polylineMesh = new THREE.LineSegments(polylineGeometry, polylineMaterial);
      polylineMesh.userData = { kind: "draft-polyline" };
      polylineMesh.renderOrder = 999;
    }
    return {
      mesh: polylineMesh,
      geom: polylineGeometry,
      mat: polylineMaterial,
    };
  }

  function ensureSegments(): {
    mesh: THREE.LineSegments;
    geom: THREE.BufferGeometry;
    mat: THREE.LineBasicMaterial;
  } {
    if (!segmentsMesh || !segmentsGeometry || !segmentsMaterial) {
      segmentsGeometry = new THREE.BufferGeometry();
      segmentsMaterial = new THREE.LineBasicMaterial({
        color: DEFAULT_PREVIEW_COLOR,
        transparent: true,
        opacity: 0.8,
        depthTest: false,
        depthWrite: false,
      });
      applyClipToMaterial(segmentsMaterial);
      segmentsMesh = new THREE.LineSegments(segmentsGeometry, segmentsMaterial);
      segmentsMesh.userData = { kind: "draft-segments" };
      segmentsMesh.renderOrder = 999;
    }
    return {
      mesh: segmentsMesh,
      geom: segmentsGeometry,
      mat: segmentsMaterial,
    };
  }

  function clearGroup(): void {
    while (group.children.length > 0) {
      group.remove(group.children[0]!);
    }
  }

  function update(input: DraftPreviewUpdate): void {
    clearGroup();
    const colorHex = input.colorHex
      ? new THREE.Color(input.colorHex).getHex()
      : DEFAULT_PREVIEW_COLOR;

    if (input.kind === "line") {
      const { mesh, geom, mat } = ensureLine();
      mat.color.setHex(colorHex);
      const pos = geom.getAttribute("position") as THREE.BufferAttribute;
      pos.setXYZ(0, input.a.x, input.a.y, 0);
      pos.setXYZ(1, input.b.x, input.b.y, 0);
      pos.needsUpdate = true;
      // B3 fix: pin the draw range to exactly the 2 vertices we just
      // wrote. Stale draw-range from a prior polyline → line transition
      // would otherwise leave the GPU painting phantom segments.
      geom.setDrawRange(0, 2);
      geom.computeBoundingSphere();
      group.add(mesh);
      return;
    }

    if (input.kind === "circle") {
      const { mesh, geom, mat } = ensureCircle();
      mat.color.setHex(colorHex);
      const segments = 64;
      const pos = geom.getAttribute("position") as THREE.BufferAttribute;
      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        pos.setXYZ(
          i,
          input.center.x + input.radius * Math.cos(angle),
          input.center.y + input.radius * Math.sin(angle),
          0,
        );
      }
      pos.needsUpdate = true;
      geom.setDrawRange(0, segments + 1);
      geom.computeBoundingSphere();
      group.add(mesh);
      return;
    }

    if (input.kind === "segments") {
      const { mesh, geom, mat } = ensureSegments();
      mat.color.setHex(colorHex);
      // LineSegments (gl.LINES) — vertex pairs, one pair per segment.
      // Disjoint segments are first-class: no "moveTo" plumbing.
      const pairCount = input.segments.length;
      const vertexCount = pairCount * 2;
      const positions = new Float32Array(vertexCount * 3);
      let w = 0;
      for (const seg of input.segments) {
        positions[w++] = seg.a.x;
        positions[w++] = seg.a.y;
        positions[w++] = 0;
        positions[w++] = seg.b.x;
        positions[w++] = seg.b.y;
        positions[w++] = 0;
      }
      const prev = geom.getAttribute("position");
      if (prev && (prev as THREE.BufferAttribute).array !== positions) {
        (prev as THREE.BufferAttribute).array = new Float32Array(0);
      }
      geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geom.computeBoundingSphere();
      geom.setDrawRange(0, vertexCount);
      group.add(mesh);
      return;
    }

    if (input.kind === "polyline") {
      const { mesh, geom, mat } = ensurePolyline();
      mat.color.setHex(colorHex);
      const pts = input.points;
      // LineSegments expects pairs: for N input points + closed flag,
      // we emit (N - 1) interior pairs + (closed ? 1 : 0) closing pair.
      // Each pair is 2 vertices × 3 floats = 6 floats. The earlier
      // line-strip path dropped the last segment on Three.js r184
      // (rectangle preview's c3 → c0 closing edge never rendered) —
      // see PR #57 repro and the bug-repro screenshots in
      // docs/ui-reference/_bug-repro/.
      const interiorPairs = Math.max(pts.length - 1, 0);
      const closingPair = input.closed && pts.length > 1 ? 1 : 0;
      const pairCount = interiorPairs + closingPair;
      const vertexCount = pairCount * 2;
      const positions = new Float32Array(vertexCount * 3);
      let w = 0;
      for (let i = 0; i < interiorPairs; i++) {
        const a = pts[i]!;
        const b = pts[i + 1]!;
        positions[w++] = a.x;
        positions[w++] = a.y;
        positions[w++] = 0;
        positions[w++] = b.x;
        positions[w++] = b.y;
        positions[w++] = 0;
      }
      if (closingPair === 1) {
        const last = pts[pts.length - 1]!;
        const first = pts[0]!;
        positions[w++] = last.x;
        positions[w++] = last.y;
        positions[w++] = 0;
        positions[w++] = first.x;
        positions[w++] = first.y;
        positions[w++] = 0;
      }
      // B3 fix (post-V9): dispose the previous position attribute
      // before swapping in the new one. R3F r184 + drei do not auto-
      // dispose attributes on `setAttribute` replacement; the dispose
      // is an explicit caller responsibility.
      const prev = geom.getAttribute("position");
      if (prev && (prev as THREE.BufferAttribute).array !== positions) {
        (prev as THREE.BufferAttribute).array = new Float32Array(0);
      }
      geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geom.computeBoundingSphere();
      // Pin draw-range to the segment-pair vertex count so partial
      // updates from a previous polyline don't paint phantom segments.
      geom.setDrawRange(0, vertexCount);
      group.add(mesh);
      return;
    }
  }

  /**
   * #234 @codex P2 — "Avoid recompiling draft preview materials on every
   * move". The caller (DraftPreviewLayer) invokes this on EVERY
   * `draftPreviewEntity` change, i.e. every pointer move, while the rect
   * itself only changes on a VP move/zoom (or a draft start/end toggle).
   * Three tiers, matched to the three.js clipping update path:
   *
   *   (c) IDENTICAL rect values (the per-pointer-move common case) → full
   *       no-op. No allocation, no material touch, no version bump.
   *   (b) Same plane COUNT (always 4 here), only VALUES changed (VP pan/zoom
   *       mid-draft) → mutate the EXISTING owned Plane constants in place,
   *       NO `needsUpdate`. #299 @codex P2 asked whether three's cached
   *       `materialProperties.clippingState` can serve a STALE projection
   *       after an in-place mutation — it cannot across any `render()`
   *       boundary; proven against the INSTALLED three r184 source:
   *         - `WebGLRenderer.js:1784-1785` — every `render()` ends with
   *           `_currentMaterialId = -1; _currentCamera = null;`.
   *         - `WebGLRenderer.js:2361-2374` — with `localClippingEnabled`
   *           (set in `Scene3DStage`) `clipping.setState(material, camera,
   *           useCache)` runs for EVERY object; `useCache = camera ===
   *           _currentCamera && material.id === _currentMaterialId` is
   *           therefore FALSE for the first draw of this material in every
   *           render.
   *         - `WebGLClipping.js:92` + `:132-157` — `useCache=false` ⇒
   *           `projectPlanes` re-reads each Plane's CURRENT normal/constant
   *           (`plane.copy(planes[i])`) into the uniform array and sets
   *           `uniform.needsUpdate = true`.
   *         - `WebGLRenderer.js:2528-2534` + `:2741` — `material.id !==
   *           _currentMaterialId(-1)` ⇒ `refreshMaterial = true` ⇒ uniform
   *           upload (`uniforms.clippingPlanes = clipping.uniform`, `:2221`).
   *         - The cached `clippingState` is served ONLY on `useCache=true` —
   *           a 2nd+ consecutive draw with the same material+camera WITHIN
   *           one pass, whose first draw already refreshed that very array
   *           (`WebGLClipping.js:88-100`, `:130-132`).
   *       The shader's `NUM_CLIPPING_PLANES` / `UNION_CLIPPING_PLANES`
   *       defines depend only on the plane COUNT, so a value-only change
   *       needs no program reprocessing. Our path renders full `render()`
   *       passes per frame (`frameloop="demand"` + `invalidate()` per move),
   *       so the next frame always re-projects. Pinned by the "r184
   *       WebGLClipping pin" test in `draftPreviewMesh.test.ts`.
   *   (a) TOGGLE null↔rect (draft start/end, VP context change) → reassign
   *       `clippingPlanes` on every existing material + `needsUpdate = true`:
   *       the plane count flips 0↔4, the `NUM_CLIPPING_PLANES` define
   *       changes, and a program recompile is legitimately needed.
   */
  function setClipRect(rect: DraftClipRect | null): void {
    const prev = currentClipRect;
    // (c) identical — both null, or all 4 bounds exactly equal → no-op. Exact
    // (non-epsilon) compare is right here: the rect is derived from the VP
    // entity's paper bounds, which are bit-stable across pointer moves and
    // only change on an actual VP edit.
    if (prev === null && rect === null) return;
    if (
      prev !== null &&
      rect !== null &&
      prev.minX === rect.minX &&
      prev.maxX === rect.maxX &&
      prev.minY === rect.minY &&
      prev.maxY === rect.maxY
    ) {
      return;
    }
    if (prev !== null && rect !== null) {
      // (b) rect → different rect: same plane count, values only. Mutate the
      // owned Plane constants in place — every material already references
      // `ownedClipPlanes` (non-null whenever `prev` is non-null). The next
      // `render()` re-projects the mutated values into the clip uniform — see
      // the r184 source proof in the doc block above (#299 @codex P2: the
      // per-render `_currentCamera = null` reset forces `useCache = false` on
      // the first draw, so `materialProperties.clippingState` can never serve
      // a stale rect).
      currentClipRect = { ...rect };
      writeRectToPlanes(ensureOwnedClipPlanes(), rect);
      return;
    }
    // (a) toggle null↔rect: (re)assign on every already-created material with
    // `needsUpdate` (define change). Materials created LATER pick the current
    // state up in their `ensure*` via `applyClipToMaterial`.
    currentClipRect = rect ? { ...rect } : null;
    if (rect) writeRectToPlanes(ensureOwnedClipPlanes(), rect);
    if (lineMaterial) applyClipToMaterial(lineMaterial);
    if (circleMaterial) applyClipToMaterial(circleMaterial);
    if (polylineMaterial) applyClipToMaterial(polylineMaterial);
    if (segmentsMaterial) applyClipToMaterial(segmentsMaterial);
  }

  function clear(): void {
    clearGroup();
  }

  function dispose(): void {
    clearGroup();
    if (lineGeometry) lineGeometry.dispose();
    if (lineMaterial) lineMaterial.dispose();
    if (circleGeometry) circleGeometry.dispose();
    if (circleMaterial) circleMaterial.dispose();
    if (polylineGeometry) polylineGeometry.dispose();
    if (polylineMaterial) polylineMaterial.dispose();
    if (segmentsGeometry) segmentsGeometry.dispose();
    if (segmentsMaterial) segmentsMaterial.dispose();
    lineMesh = null;
    lineGeometry = null;
    lineMaterial = null;
    circleMesh = null;
    circleGeometry = null;
    circleMaterial = null;
    polylineMesh = null;
    polylineGeometry = null;
    polylineMaterial = null;
    segmentsMesh = null;
    segmentsGeometry = null;
    segmentsMaterial = null;
    // Drop the owned clip Planes too (plain JS objects, no GPU resources —
    // released for symmetry so a disposed handle holds no THREE references).
    ownedClipPlanes = null;
    currentClipRect = null;
    if (group.parent) group.parent.remove(group);
  }

  return { group, update, setClipRect, clear, dispose };
}
